// Shared truckload-split helpers — used by the Hero estimator, the Services
// hub, the Product page, and anywhere else we add multi-load orders to the
// cart. Centralizing keeps the per-load minimum billing rules and the
// multi-delivery line-item split consistent across every entry point.
//
// 50/50 Mix is classified HEAVY (12 yd full load) — even though the name
// reads "mix" with topsoil, the blend is dense enough (sand + topsoil)
// that it ships in the same 12-yd truckload as rock and aggregates.
export function isLightMaterial(name: string): boolean {
  const n = name.toLowerCase()
  return (
    n.includes('bark') ||
    n.includes('compost') ||
    n.includes('topsoil') ||
    n.includes('play chips')
  )
}
export function getFullLoad(name: string): number {
  return isLightMaterial(name) ? 15 : 12
}

// The default yardage we pre-fill in quote forms and the product detail
// page's quantity selector. Soil/Mulch/Org. products ride in light 15-yd
// truckloads and customers typically order a full load of soil or bark
// for spring projects, so we default the dropdown to 15. Everything else
// (rock, drain rock, quarry, decor stone, 50/50 Mix) defaults to 12 — a
// standard heavy truckload.
export function getDefaultQuantity(productName: string): string {
  if (!productName) return '12'
  const n = productName.toLowerCase()
  // Fill dirt + 50/50 Mix are heavy: they ship 12 yd/load like the
  // aggregates, not 15 yd like bark/compost/topsoil.
  const isSoilMulchOrg =
    n.includes('topsoil') ||
    n.includes('bark') ||
    n.includes('compost') ||
    n.includes('play chips')
  return isSoilMulchOrg ? '15' : '12'
}

// Minimum billable yards for a STANDALONE order (single delivery). We accept
// orders as small as 3 yd so homeowners with planter beds, paver bases, and
// small drainage repairs can use us. Anything smaller is bumped up to the
// 3 yd order minimum — trucks have fixed dispatch costs and 1–2 yd orders
// don't cover them. Partial last loads in a multi-delivery split are NOT
// bumped — if a customer is already paying for at least one full load, the
// partial is billed at its actual yardage (with the small-load surcharge
// applied per `getSmallLoadSurcharge` below).
// Small-load surcharge threshold per material type. Below this many yards
// in a single load, we add a surcharge to compensate for fixed dispatch
// cost. Threshold is 10 yd for ALL materials — once a customer is at or
// above 10 yd, they get base price whether the truck is light- or
// heavy-loaded. Light materials still ride in larger 15 yd loads and
// still qualify for the 5%-at-15-yd bulk discount via CartContext.
export function getSmallLoadThreshold(_productName: string): number {
  return 10
}

/**
 * Compute the delivery fee for a specific load.
 * Loads under 3 yards get a flat $250 delivery fee to cover dispatch costs.
 * Loads 3 yards and up get the standard zone delivery fee.
 */
export function getLoadDeliveryFee(yards: number, zoneFee: number): number {
  if (yards > 0 && yards < 3) return 250
  return zoneFee
}

/**
 * Compute the small-load price surcharge multiplier for a given yardage.
 *
 * Curve:
 * - At or above threshold (10 heavy): 1.0× (no surcharge)
 * - Linear +10% per yard short of the threshold down to 3 yd.
 * - Under 3 yd: 1.0× (no surcharge, because the $250 flat delivery fee covers dispatch).
 */
export function getSmallLoadSurcharge(
  yards: number,
  productName: string,
): number {
  if (yards < 3) return 1 // Flat $250 delivery fee handles the economics for < 3yd
  const threshold = getSmallLoadThreshold(productName)
  if (yards >= threshold) return 1
  return 1 + (threshold - yards) * 0.1
}

/**
 * Split a total yardage into per-delivery quantities.
 *
 * Rules:
 * - Loads 1..N-1 are always fullLoad yards.
 * - The last load is the actual remainder.
 *
 * Returns an array of yards per delivery. e.g. for non-light material:
 *   splitIntoDeliveries(16) → [12, 4]
 *   splitIntoDeliveries(14) → [12, 2]
 *   splitIntoDeliveries(20) → [12, 8]
 *   splitIntoDeliveries(9)  → [9]
 *   splitIntoDeliveries(2)  → [2]
 */
export function splitIntoDeliveries(
  totalYards: number,
  productName: string,
): number[] {
  const fullLoad = getFullLoad(productName)
  const total = Math.max(0, Math.round(totalYards))
  if (total <= 0) return []
  if (total <= fullLoad) {
    return [total]
  }
  const deliveries = Math.ceil(total / fullLoad)
  const lastLoad = total - (deliveries - 1) * fullLoad
  const loads: number[] = []
  for (let i = 0; i < deliveries - 1; i++) loads.push(fullLoad)
  loads.push(lastLoad)
  return loads
}

/**
 * Convenience: turn a split result into cart-line-item descriptors so callers
 * can iterate and addToCart() consistently. The product name gets a
 * "(Delivery N)" suffix when there's more than one load, matching the cart
 * drawer's per-delivery breakdown rendering.
 */
export function buildDeliveryLineItems(
  totalYards: number,
  productName: string,
): { product: string; quantity: number }[] {
  const loads = splitIntoDeliveries(totalYards, productName)
  if (loads.length === 0) return []
  if (loads.length === 1) {
    return [
      {
        product: productName,
        quantity: loads[0],
      },
    ]
  }
  return loads.map((qty, i) => ({
    product: `${productName} (Delivery ${i + 1})`,
    quantity: qty,
  }))
}
